import {Injectable} from '@angular/core';
import {
  ProjectChapterType,
  ProjectRoleType,
  ProjectStepType,
  ProjectUserDetailResponse,
} from '@frontend/shared-api';
import {Chapter} from '../project-step.types';
import {CHAPTERS_IN_DEVELOPMENT, SPECIAL_OPTION_IDS} from '../project-step-state.constants';

type RedirectTarget =
  {chapterId: ProjectChapterType; stepId: ProjectStepType} | 'user-management' | null;

@Injectable({
  providedIn: 'root',
})
export class ProjectSideMenuAccessControlService {
  isChapterInDevelopment(chapterId: ProjectChapterType): boolean {
    return CHAPTERS_IN_DEVELOPMENT.includes(chapterId);
  }

  isStepAccessible(
    chapterId: ProjectChapterType,
    stepId: ProjectStepType,
    userDetail?: ProjectUserDetailResponse,
  ): boolean {
    if (!userDetail) return false;

    if (this.isChapterInDevelopment(chapterId)) {
      return false;
    }

    const userRoles = new Set(userDetail.roles ?? []);
    if (userRoles.has(ProjectRoleType.PROJECT_MANAGER)) return true;

    const accessibleSteps = new Set(userDetail.accessibleSteps ?? []);
    return accessibleSteps.has(stepId);
  }

  isChapterAccessible(
    chapter: Chapter,
    userDetail: ProjectUserDetailResponse | undefined,
  ): boolean {
    if (this.isChapterInDevelopment(chapter.configId)) {
      return false;
    }

    return chapter.steps.some((step) => {
      return this.isStepAccessible(chapter.configId, step.configId, userDetail);
    });
  }

  getRedirectTarget(
    chapters: Chapter[],
    userDetail: ProjectUserDetailResponse | undefined,
    currentActiveStep: {chapterId: ProjectChapterType; stepId: ProjectStepType} | null,
  ): RedirectTarget {
    if (!chapters.length || !userDetail || !currentActiveStep) {
      return null;
    }

    const isCurrentStepAccessible = this.isStepAccessible(
      currentActiveStep.chapterId,
      currentActiveStep.stepId,
      userDetail,
    );
    if (isCurrentStepAccessible) {
      return null;
    }

    const firstAccessibleStep = this.findFirstAccessibleStep(chapters, userDetail);
    if (firstAccessibleStep) {
      return firstAccessibleStep;
    }

    const userRoles = new Set(userDetail.roles ?? []);
    if (userRoles.has(ProjectRoleType.PROJECT_MANAGER)) {
      return SPECIAL_OPTION_IDS.USER_MANAGEMENT;
    }

    return null;
  }

  findFirstAccessibleStep(
    chapters: Chapter[],
    userDetail: ProjectUserDetailResponse | undefined,
  ): {chapterId: ProjectChapterType; stepId: ProjectStepType} | null {
    for (const chapter of chapters) {
      if (this.isChapterInDevelopment(chapter.configId)) {
        continue;
      }

      for (const step of chapter.steps) {
        if (this.isStepAccessible(chapter.configId, step.configId, userDetail)) {
          return {chapterId: chapter.configId, stepId: step.configId};
        }
      }
    }
    return null;
  }
}
